import HDCheckBox from "@/common/components/HDCheckBox";
import { useAppDispatch, useAppSelector } from "@/common/hooks/useRedux";
import { BroadcastEvents, DocumentType, DoctorDocumentTypeContext } from "@/common/utils/types";
import { useRealtimeContextApi } from "@/state/providers/RealtimeProvider";
import {
    addDocumentForDoctor,
    removeDocumentForDoctor,
    addHistoryDocumentForDoctor,
    removeHistoryDocumentForDoctor,
} from "@/state/redux/documentSlice";
import { getPatientData, getPatientVisits } from "@/features/patient-view/state/redux/activePatientDataSlice";
import { Combobox } from "@headlessui/react";
import { useEffect, useRef, useCallback, useState } from "react";
import { MONITORING_DOCUMENT_TYPE_ID, excludeMonitoringId } from "@/common/utils/monitoring";
import { useAccountContext } from "@/state/providers/AccountProvider";

const WidgetDocumentSelect: React.FC = () => {
    const firstSelectedDocRef = useRef<HTMLDivElement>(null);
    const hasAutoScrolled = useRef(false);

    const dispatch = useAppDispatch();

    const { selectedDocumentsRef, emitEvent } = useRealtimeContextApi();
    const {
        selectedDocuments: selectedDocumentIds,
        selectedHistoryDocuments: selectedHistoryDocumentIds,
        documentTypes,
        hiddenDocuments,
    } = useAppSelector((state) => state.documents);
    const { selectedDoctor } = useAppSelector((state) => state.doctors);
    const { accountData } = useAccountContext();

    // Get current patient data to determine if we're in a history visit
    const patientData = useAppSelector(getPatientData);
    const allVisits = useAppSelector(getPatientVisits);

    // Determine if we're in a history visit
    const isHistory = allVisits.find((visit) => visit.id === patientData?.id)?.isHistory;
    const context = isHistory ? DoctorDocumentTypeContext.History : DoctorDocumentTypeContext.Default;

    // Use the appropriate selected documents based on context
    const selectedDocumentIdsForContext =
        context === DoctorDocumentTypeContext.History ? selectedHistoryDocumentIds : selectedDocumentIds;

    // Display-only: this widget's checkbox list already excludes Monitoring (see visibleDocs
    // below), but selectedDocumentIdsForContext is the SAME shared Redux state the main sidebar's
    // DocumentSelect can put Monitoring into. The "N selected" count must not count something this
    // widget can never actually generate — see excludeMonitoringId's own comment. Deliberately NOT
    // used for the add/remove mutation logic below (handleSetSelectedDocuments, the effect dep,
    // Combobox's value/docIsSelected checks) — those operate on the real shared selection as-is.
    const visibleSelectedDocumentIds = excludeMonitoringId(selectedDocumentIdsForContext);

    const [filteredDocs, setFilteredDocs] = useState<DocumentType[]>(documentTypes);

    useEffect(() => {
        // filter out hidden, inactive, and (unless the account has opted in) Monitoring
        // documents. Unlike the main sidebar's DocumentSelect (which has GenerateButton's
        // explicit monitoringSelected split to route Monitoring to the retroactive batch
        // endpoint), this widget has no such trigger — selecting Monitoring here can only ever
        // feed generate-on-stop, where it's filtered out anyway (see transcribeAudio). Excluding
        // it here avoids offering a checkbox that silently has no effect. Also gated behind
        // account_settings.monitoring_enabled, same as DocumentSelect.tsx, for the gradual,
        // admin-controlled rollout (set via spork).
        const visibleDocs = documentTypes.filter(
            (doc: DocumentType) =>
                !hiddenDocuments.includes(doc?.id || "") &&
                doc?.is_active &&
                (doc?.id !== MONITORING_DOCUMENT_TYPE_ID || accountData?.monitoring_enabled === true),
        );

        // separate selected documents and others
        const selectedDocs = visibleDocs.filter((doc: DocumentType) =>
            selectedDocumentIdsForContext.includes(doc?.id || ""),
        );
        const otherDocs = visibleDocs.filter(
            (doc: DocumentType) => !selectedDocumentIdsForContext.includes(doc?.id || ""),
        );

        // sort both arrays
        selectedDocs.sort((a, b) => a.document_type_name.localeCompare(b.document_type_name));
        otherDocs.sort((a, b) => a.document_type_name.localeCompare(b.document_type_name));

        // combine the arrays with selected documents pinned at the start
        setFilteredDocs([...selectedDocs, ...otherDocs]);
    }, [documentTypes, hiddenDocuments, selectedDocumentIdsForContext, accountData?.monitoring_enabled]);

    // handle setting selected documents
    const handleSetSelectedDocuments = useCallback(
        async (documentIds: string[]) => {
            // get the ids of the new documents
            const newIds = documentIds.filter((id) => id !== null && id !== undefined);

            // determine which documents need to be added or removed
            const documentsToAdd = newIds.filter((id) => !selectedDocumentIdsForContext.includes(id));
            const documentsToRemove = selectedDocumentIdsForContext.filter((id: string) => !newIds.includes(id));

            // add new documents
            for (const id of documentsToAdd) {
                if (context === DoctorDocumentTypeContext.History) {
                    await dispatch(addHistoryDocumentForDoctor({ documentTypeId: id }));
                } else {
                    await dispatch(addDocumentForDoctor({ documentTypeId: id }));
                }

                if (selectedDoctor) {
                    await emitEvent(BroadcastEvents.documentSelection, {
                        documentTypeId: id,
                        remove: false,
                        doctorId: selectedDoctor?.id,
                        context: context,
                    });
                }
            }

            // remove deselected documents
            for (const id of documentsToRemove) {
                if (context === DoctorDocumentTypeContext.History) {
                    await dispatch(removeHistoryDocumentForDoctor({ documentTypeId: id }));
                } else {
                    await dispatch(removeDocumentForDoctor({ documentTypeId: id }));
                }

                if (selectedDoctor) {
                    await emitEvent(BroadcastEvents.documentSelection, {
                        documentTypeId: id,
                        remove: true,
                        doctorId: selectedDoctor?.id,
                        context: context,
                    });
                }
            }

            // update the selected documents ref and component state
            selectedDocumentsRef.current = newIds;
        },
        [dispatch, selectedDocumentIdsForContext, selectedDocumentsRef],
    );

    // scroll to the first selected document
    useEffect(() => {
        // if we have already auto-scrolled, don't do it again
        if (hasAutoScrolled.current) return;

        const timeout = setTimeout(() => {
            if (firstSelectedDocRef.current) {
                firstSelectedDocRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
            hasAutoScrolled.current = true;
        }, 200);

        return () => {
            clearTimeout(timeout);
        };
    }, [selectedDocumentIdsForContext]);

    // find the index of the first selected document in filteredDocs
    const firstSelectedIndex = filteredDocs.findIndex((doc) => selectedDocumentIdsForContext.includes(doc.id));

    return (
        <div data-tour="widget-document-select" className="w-full h-[204px] bg-shadow-950">
            <div className="w-full h-[34px] bg-shadow-800 self-stretch pl-6 text-white text-xs font-normal flex flex-row items-center">
                <div>SELECTED DOCUMENTS ({visibleSelectedDocumentIds.length})</div>
            </div>
            <div className="px-6">
                <Combobox value={selectedDocumentIdsForContext} onChange={() => {}} multiple>
                    <div className="relative w-full text-white max-h-[170px] overflow-auto">
                        {filteredDocs.map((doc, index) => {
                            const docIsSelected = selectedDocumentIdsForContext.includes(doc.id);
                            const toggleDoc = () => {
                                if (docIsSelected) {
                                    handleSetSelectedDocuments(
                                        selectedDocumentIdsForContext.filter((item) => item !== doc.id),
                                    );
                                } else {
                                    handleSetSelectedDocuments([...selectedDocumentIdsForContext, doc.id]);
                                }
                            };
                            return (
                                <div
                                    key={doc.id}
                                    data-testid="document-template"
                                    ref={index === firstSelectedIndex ? firstSelectedDocRef : null}
                                    role="button"
                                    tabIndex={0}
                                    aria-pressed={docIsSelected}
                                    className="flex flex-row items-center gap-3 cursor-pointer"
                                    onClick={toggleDoc}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            toggleDoc();
                                        }
                                    }}
                                >
                                    <div>
                                        <HDCheckBox isChecked={docIsSelected} />
                                    </div>
                                    <div className="py-2.5 text-white text-sm font-normal leading-tight">
                                        {doc.document_type_name}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Combobox>
            </div>
        </div>
    );
};

export default WidgetDocumentSelect;
